import {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Default,
  Switch,
  Case,
  When,
} from 'react-if';
import { useNavigate } from 'react-router';
import { Tooltip } from '@mui/material';
import { ExclamationCircleIcon, StarIcon } from '@heroicons/react/20/solid';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  DocumentCheckIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { OrganisationFeatureFlags } from 'shared';
import { Link, useLocation } from 'react-router-dom';

import { useNavigationOpts } from '../../hooks/useNavigationOpts';
import { WrappedLink } from '../WrappedLink';
import { classNames }  from '../../../common/lib/classNames';
import { useHasValidSubscription } from '../../hooks/useHasValidSubscription';
import { Badge } from '../../../common/Atoms/Badge';
import { useGetSubscriptionMetadata } from '../../hooks/useGetSubscription';
import { UpgradeBadge } from '../Molecules/UpgradeBadge';
import { Divider } from '../../../common/Atoms/Divider';
import { useCreateGroupMutation } from '../../services/api/groupApi/group';
import { useGroups } from '../../hooks/useGroups';
import { useModals } from '../../hooks/useModals';
import { mixpanelService } from '../../../common/lib/mixpanel';
import { useSidebarLayout } from '../../hooks/useSidebarLayout';

import { Logo } from './Logo';

const FLYOUT_OPEN_DELAY_MS = 150;

export function DesktopSidebar({ currentOrg }) {
  const { navigation } = useNavigationOpts(!!currentOrg, { onlyDefaultGroup: true });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { subscriptionMetadata } = useGetSubscriptionMetadata();
  const hasValidSubscription = useHasValidSubscription();
  const [createGroup] = useCreateGroupMutation();
  const { refetch } = useGroups();
  const { show } = useModals();
  const { collapsed, toggleCollapsed } = useSidebarLayout();
  // Hovering the collapsed rail temporarily expands the nav as an overlay
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  // Expandable sections' open state lives here (not in an uncontrolled
  // Disclosure) so the collapsed rail and the hover flyout - two renders of
  // the same panel - stay in sync and rows keep identical positions
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const isSectionOpen = item => openSections[item.name] ?? !!item.alwaysOpen;
  const toggleSection = item => setOpenSections(s => ({
    ...s,
    [item.name]: !(s[item.name] ?? !!item.alwaysOpen),
  }));
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Mirror of `collapsed` so the pending hover timer sees the current value
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;

  useEffect(() => {
    // Close the hover flyout after navigating, and drop any pending
    // hover-open so it can't fire on the new page
    clearTimeout(hoverTimer.current);
    setFlyoutOpen(false);
  }, [pathname]);

  useEffect(() => {
    return () => clearTimeout(hoverTimer.current);
  }, []);

  function onRailMouseEnter() {
    if (!collapsed) {
      return;
    }

    hoverTimer.current = setTimeout(() => {
      // The sidebar may have expanded while the timer was pending
      if (collapsedRef.current) {
        setFlyoutOpen(true);
      }
    }, FLYOUT_OPEN_DELAY_MS);
  }

  function onRailMouseLeave() {
    clearTimeout(hoverTimer.current);
    setFlyoutOpen(false);
  }

  function onToggleClick() {
    clearTimeout(hoverTimer.current);
    setFlyoutOpen(false);
    toggleCollapsed();
  }

  const noSub = item => {
    return (!hasValidSubscription && item.requiresSubscription);
  };

  const upgrade = item => {
    if (hasValidSubscription) {
      return item.requiresPaidFeature && !subscriptionMetadata?.features?.[item.requiresPaidFeature];
    }
  };

  const notAllowed = item => {
    return noSub(item) || upgrade(item);
  };

  async function onNewGroup() {
    const res = await createGroup({ organisationId: currentOrg.id }).unwrap();

    const createdId = res?.id;

    if (refetch) {
      // Force ensure the group is in the cache
      await refetch();
    }

    if (createdId) {
      navigate(`${currentOrg.id}/groups/${createdId}`);
    }
  }

  function onBookDemo() {
    show(`call`);
    mixpanelService.demoCallClicked();
  }

  // Single nav panel used for every state. The collapsed rail renders this
  // same w-64 panel clipped to w-16 with labels `invisible` (still occupying
  // layout), so row geometry is identical between the rail, the expanded
  // sidebar and the hover flyout by construction.
  const navPanel = ({ labels, inFlyout }: { labels: boolean; inFlyout: boolean }) => {
    // `invisible` (not `hidden`) keeps the element's layout footprint
    const hide = labels ? `` : `invisible`;

    return (
      <>
        <div className={ `flex shrink-0 items-center px-4` }>
          { labels
            ? <Logo />
            : (
              <img
                className={ `h-8 w-8` }
                src={ `/favicon.ico` }
                alt={ `Paidnice` }
              />
            ) }
        </div>
        <div className={ `mt-5 flex min-h-0 grow flex-col overflow-y-auto` }>
          <nav className={ `flex-1 space-y-8 bg-white px-2` }
            aria-label={ `Sidebar` }>
            <div className={ `space-y-1` }>
              { navigation.map((item, i) => {
                const isGroupOption = item.href.startsWith(`/groups/`);
                const LinkComponent = item.absolute ? Link : WrappedLink;

                return (
                  <Switch key={ i }>
                    <Case condition={ item.href === `ADD_NEW_GROUP` }>
                      <a
                        key={ item.name }
                        href={ `#` }

                        className={ classNames(
                          item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                          `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                          notAllowed(item) && `opacity-50 cursor-not-allowed`,
                        ) }
                        onClick={ e => {
                          e.preventDefault();
                          onNewGroup();
                        } }
                      >
                        <>
                          <item.icon
                            className={ classNames(
                              item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                              `mr-3 shrink-0 h-6 w-6`,
                            ) }
                            aria-hidden={ `true` }
                          />
                          <span className={ hide }>{ item.name }</span>
                        </>
                      </a>
                    </Case>
                    <Case condition={ item.href === `BOOK_DEMO` }>
                      <a
                        key={ item.name }
                        href={ `#` }

                        className={ classNames(
                          item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                          `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                          notAllowed(item) && `opacity-50 cursor-not-allowed`,
                        ) }
                        onClick={ e => {
                          e.preventDefault();
                          onBookDemo();
                        } }
                      >
                        <>
                          <item.icon
                            className={ classNames(
                              item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                              `mr-3 shrink-0 h-6 w-6`,
                            ) }
                            aria-hidden={ `true` }
                          />
                          <span className={ hide }>{ item.name }</span>
                        </>
                      </a>
                    </Case>
                    <Case condition={ item.href === `SPACE` }>
                      { /* Hide only the divider's label when collapsed; the rule stays visible */ }
                      <div className={ labels ? `` : `[&_span]:invisible` }>
                        <Divider
                          weight={ `light` }
                          text={ item.name }
                          spacing={ `xs` }
                        />
                      </div>
                    </Case>
                    <Case condition={ item.external }>
                      <a
                        href={ item.href }
                        target={ `_blank` }
                        rel={ `noreferrer` }
                        className={ classNames(
                          item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                          `group flex items-center px-2 py-2 text-sm font-medium rounded-md`,
                          notAllowed(item) && `opacity-50 cursor-not-allowed`,
                        ) }>
                        <item.icon
                          className={ classNames(
                            item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                            `mr-3 shrink-0 h-6 w-6`,
                          ) }
                          aria-hidden={ `true` }
                        />
                        <span className={ hide }>{ item.name }</span>
                      </a>
                    </Case>
                    <Case condition={ !item.children }>
                      <LinkComponent
                        key={ item.name }
                        to={ noSub(item)? `` : item.href }
                        className={ classNames(
                          item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                          `group flex px-2 py-2 text-sm font-medium rounded-md`,
                          notAllowed(item) && `opacity-50 cursor-not-allowed`,
                          isGroupOption ? `flex-col` : `items-center`,
                        ) }
                        disabled={ notAllowed(item) }
                      >
                        <span className={ `flex` }>
                          <item.icon
                            className={ classNames(
                              item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                              `mr-3 shrink-0 h-6 w-6`,
                            ) }
                            aria-hidden={ `true` }
                          />
                          <Tooltip title={ item.name }>
                            <span className={ classNames(`flex-1 truncate`, hide) }>{ item.name }</span>
                          </Tooltip>
                          <When condition={ noSub(item) }>
                            <span className={ hide }>
                              <Badge color={ `gray` }
                                message={ `No Plan` }
                                size={ `xs` } />
                            </span>
                          </When>

                          <When condition={ item.type === `groupOptionDefault` }>
                            <div className={ classNames(`ml-0.5`, hide) }>
                              <Tooltip title={ `Default Group` }>
                                <StarIcon className={ `text-yellow-400 h-5 w-5` } />
                              </Tooltip>
                            </div>
                          </When>
                        </span>
                        { /* Group related stuffs */ }
                        <When condition={ isGroupOption }>
                          <div className={ classNames(`flex mt-1`, hide) }>

                            <When condition={ item.memberCount !== undefined }>
                              <Tooltip title={ `Group contains ${item.memberCount} contacts` }>
                                <span className={ `mx-1 flex` }>
                                  { item.memberCount }
                                  <UsersIcon className={ `text-gray-400 h-5 w-5` } />
                                </span>
                              </Tooltip>
                            </When>

                            <When condition={ item.validCount !== undefined }>
                              <Tooltip title={ `Group contains ${item.validCount} policies` }>
                                <span className={ `mx-1 flex` }>
                                  { item.validCount }
                                  <DocumentCheckIcon className={ `text-gray-400 h-5 w-5` } />
                                </span>
                              </Tooltip>
                            </When>

                            <When condition={ item.invalidCount }>
                              <Tooltip title={ `Group has ${item.invalidCount} incomplete policies` }>
                                <span className={ `mx-1 flex text-red-700` }>
                                  { item.invalidCount }
                                  <ExclamationCircleIcon className={ `text-red-700 h-5 w-5` } />
                                </span>
                              </Tooltip>
                            </When>

                          </div>
                        </When>
                        <When condition={ upgrade(item) }>
                          <span className={ hide }>
                            <UpgradeBadge />
                          </span>
                        </When>

                        <When condition={ !!item.badgeCount }>
                          <Tooltip title={ `${item.badgeCount} unread` }>
                            <Badge
                              className={ classNames(`mt-0 ml-auto`, hide) }
                              color={ `green` }
                            >
                              { item.badgeCount }
                            </Badge>
                          </Tooltip>
                        </When>

                        <When condition={
                          currentOrg?.featureFlags?.includes(OrganisationFeatureFlags.AI_AGENT)
                          && item.href === `/agents`
                        }>

                          <Tooltip title={ `You have early access to this feature. It may not be perfect so please let us know if you have any feedback!` }>
                            <Badge
                              className={ classNames(`mt-0 ml-1`, hide) }
                              color={ `blue` }
                            >
                              { `Early Access` }
                            </Badge>
                          </Tooltip>
                        </When>

                      </LinkComponent>
                    </Case>
                    <Default>
                      <div className={ `space-y-1 w-full` }
                        key={ item.name }>
                        <a
                          className={ classNames(
                            item.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                            `group flex items-center px-2 py-2 text-sm font-medium rounded-md cursor-pointer`,
                          ) }
                          onClick={ () => toggleSection(item) }
                        >
                          <item.icon
                            className={ classNames(
                              item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                              `mr-3 shrink-0 h-6 w-6`,
                            ) }
                            aria-hidden={ `true` }
                          />
                          <span className={ classNames(`flex-1`, hide) }>{ item.name }</span>

                          <svg
                            className={ classNames(
                              isSectionOpen(item) ? `text-gray-400 rotate-90` : `text-gray-300`,
                              `ml-3 h-5 w-5 shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-gray-400`,
                              hide,
                            ) }
                            viewBox={ `0 0 20 20` }
                            aria-hidden={ `true` }
                          >
                            <path d={ `M6 6L14 10L6 14V6Z` }
                              fill={ `currentColor` } />
                          </svg>
                        </a>
                        { /* Children indent past the rail width, so hide the whole
                             panel when collapsed - it still occupies its height */ }
                        <When condition={ isSectionOpen(item) }>
                          <div className={ classNames(`space-y-1`, hide) }>
                            { /* Optional-chain: react-if evaluates this branch eagerly even for childless items */ }
                            { item.children?.map(subItem => (
                              <Switch key={ subItem.key || subItem.name }>
                                <Case condition={ subItem.href === `ADD_NEW_GROUP` }>
                                  <a
                                    key={ subItem.name }
                                    href={ `#` }
                                    className={ classNames(
                                      subItem.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                                      `group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11`,
                                      notAllowed(subItem) && `opacity-50 cursor-not-allowed`,
                                    ) }
                                    onClick={ e => {
                                      e.preventDefault();
                                      onNewGroup();
                                    } }
                                  >
                                    <>
                                      <subItem.icon
                                        className={ classNames(
                                          item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                          `mr-3 shrink-0 h-6 w-6`,
                                        ) }
                                        aria-hidden={ `true` }
                                      />
                                      { subItem.name }
                                    </>
                                  </a>
                                </Case>
                                <Default>
                                  <WrappedLink
                                    key={ subItem.name }
                                    to={ subItem.href }
                                    className={ classNames(
                                      subItem.current ? `bg-gray-100 text-gray-900` : `text-gray-600 hover:bg-gray-50 hover:text-gray-900`,
                                      `group flex items-center px-2 py-2 text-sm font-medium rounded-md pl-11`,
                                      notAllowed(subItem) && `opacity-50 cursor-not-allowed`,
                                    ) }
                                    disabled={ notAllowed(subItem) }
                                  >
                                    <>
                                      <subItem.icon
                                        className={ classNames(
                                          item.current ? `text-gray-500` : `text-gray-400 group-hover:text-gray-500`,
                                          `mr-3 shrink-0 h-6 w-6`,
                                        ) }
                                        aria-hidden={ `true` }
                                      />
                                      { subItem.name }
                                      <When condition={ subItem.type === `groupOptionDefault` }>
                                        <div className={ `ml-0.5` }>
                                          <Badge
                                            color={ `blue` }
                                            message={ `Default` }
                                            size={ `xs` }

                                          />
                                        </div>
                                      </When>
                                      <When condition={ noSub(subItem) }>
                                        <Badge color={ `gray` }
                                          message={ `No Plan` }
                                          size={ `xs` } />
                                      </When>
                                      <When condition={ upgrade(subItem) }>
                                        <UpgradeBadge />
                                      </When>
                                    </>
                                  </WrappedLink>
                                </Default>
                              </Switch>
                            )) }
                          </div>
                        </When>
                      </div>
                    </Default>
                  </Switch>
                );
              }) }
            </div>
          </nav>
        </div>
        { /* Collapse / pin toggle */ }
        <div className={ `mt-2 shrink-0 border-t border-gray-200 px-2 pt-2` }>
          <button
            type={ `button` }
            onClick={ onToggleClick }
            className={ `group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900` }
          >
            { (inFlyout || !labels)
              ? (
                <>
                  <ChevronDoubleRightIcon
                    className={ `mr-3 h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-500` }
                    aria-hidden={ `true` }
                  />
                  <span className={ hide }>{ inFlyout ? `Keep expanded` : `Expand` }</span>
                </>
              )
              : (
                <>
                  <ChevronDoubleLeftIcon
                    className={ `mr-3 h-5 w-5 shrink-0 text-gray-400 group-hover:text-gray-500` }
                    aria-hidden={ `true` }
                  />
                  { `Collapse` }
                </>
              ) }
          </button>
        </div>
      </>
    );
  };

  return (
    <div className={ `hidden md:fixed md:inset-y-0 md:z-20 md:flex` }>
      <div
        className={ `relative` }
        onMouseEnter={ onRailMouseEnter }
        onMouseLeave={ onRailMouseLeave }
      >
        <div className={ classNames(
          `h-full overflow-hidden border-r border-gray-200 bg-white`,
          collapsed ? `w-16` : `w-64`,
        ) }>
          <div className={ `flex h-full w-64 flex-col pt-5 pb-4` }>
            { navPanel({ labels: !collapsed, inFlyout: false }) }
          </div>
        </div>
        { /* Hover flyout - overlays the nested sidebar / content instead of pushing them */ }
        <When condition={ collapsed }>
          <div className={ classNames(
            `absolute inset-y-0 left-0 z-20 flex w-64 flex-col border-r border-gray-200 bg-white pt-5 pb-4 shadow-xl transform transition-all duration-150 ease-out`,
            flyoutOpen ? `translate-x-0 opacity-100` : `pointer-events-none -translate-x-2 opacity-0`,
          ) }>
            { navPanel({ labels: true, inFlyout: true }) }
          </div>
        </When>
      </div>
    </div>
  );
}
